import { User } from '../types/user';
import { 
  UserPermission, 
  UserWithPermissions,
  Organization,
  OrganizationCreateInput,
  Client,
  ClientCreateInput,
  Database,
  DatabaseCreateInput,
  AdminStats,
  RoleStats,
  DatabaseAccess,
  DatabaseAccessCreateInput,
  DatabaseAccessUpdateInput,
  DatabaseAccessBulkInput,
  UserDatabaseAccessSummary,
  DatabaseUserAccessSummary,
  BulkAccessResult
} from '../types/admin';
import axiosInstance from './axiosInstance';
import axios from 'axios';

const DEFAULT_API_URL = process.env.REACT_APP_API_URL || "https://api.pandora-ai.it/auth";

// Базовый URL без /auth для semantic layer
const getBaseApiUrl = (): string => {
  const defaultUrl = process.env.REACT_APP_API_URL || "https://api.pandora-ai.it/auth";
  return defaultUrl.replace('/auth', '');
};

// Создаем отдельный axios instance ТОЛЬКО для semantic layer запросов
// ВСЕГДА использует дефолтный API БЕЗ /auth (https://api.pandora-ai.it)
const semanticLayerApiClient = axios.create({
  baseURL: getBaseApiUrl(),
});

semanticLayerApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['ngrok-skip-browser-warning'] = 'true';
  return config;
});

// Re-export User type
export type { User };

// Хелпер для получения admin токена из localStorage
const getAdminToken = (): string | null => {
  return localStorage.getItem('adminToken');
};

// Общие заголовки для админских запросов
const getAdminHeaders = () => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin token not found. Please log in as admin first.');
  }
  return {
    'Content-Type': 'application/json',
    'x-admin-token': adminToken
  };
};

// Проверка наличия admin token
export const hasAdminToken = (): boolean => {
  return getAdminToken() !== null;
};

// Получение списка всех пользователей
export const fetchUsers = async (): Promise<User[]> => {
  const response = await axiosInstance.get('/admin/users', {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Получение списка пользователей с чувствительными данными
export const fetchUsersWithSensitive = async (includeSensitive: boolean = false): Promise<User[]> => {
  const response = await axiosInstance.get(`/admin/users/full?include_sensitive=${includeSensitive}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Получение разрешений конкретного пользователя
export const fetchUserPermissions = async (userId: number): Promise<UserPermission> => {
  const response = await axiosInstance.get(`/admin/permissions/user/${userId}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Обновление разрешений пользователя
export const updateUserPermissions = async (userId: number, permissions: Partial<UserPermission>): Promise<UserPermission> => {
  const response = await axiosInstance.put(`/admin/permissions/user/${userId}`, permissions, {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Получение списка пользователей вместе с их разрешениями
export const fetchUsersWithPermissions = async (): Promise<UserWithPermissions[]> => {
  const response = await axiosInstance.get('/admin/users-with-permissions', {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Создание нового пользователя
export const createUser = async (userData: {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  role?: string;
  client_id?: number;
  organization_id?: number;
  is_active?: boolean;
  language?: string; // IT, FR, EN
}): Promise<User> => {
  const response = await axiosInstance.post('/admin/users', userData, {
    headers: getAdminHeaders()
  });
  return response.data.user || response.data;
};

// Удаление пользователя
export const deleteUser = async (userId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/users/${userId}`, {
    headers: getAdminHeaders()
  });
};

// ============================================
// 🌍 ORGANIZATIONS API
// ============================================

export const fetchOrganizations = async (): Promise<Organization[]> => {
  const response = await axiosInstance.get('/admin/organizations', {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const createOrganization = async (data: OrganizationCreateInput): Promise<Organization> => {
  const response = await axiosInstance.post('/admin/organizations', data, {
    headers: getAdminHeaders()
  });
  return response.data.organization;
};

export const updateOrganization = async (orgId: number, data: Partial<OrganizationCreateInput>): Promise<Organization> => {
  const response = await axiosInstance.put(`/admin/organizations/${orgId}`, data, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const deleteOrganization = async (orgId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/organizations/${orgId}`, {
    headers: getAdminHeaders()
  });
};

// ============================================
// 🏢 CLIENTS API
// ============================================

export const fetchClients = async (): Promise<Client[]> => {
  const response = await axiosInstance.get('/admin/clients', {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const createClient = async (data: ClientCreateInput): Promise<Client> => {
  const response = await axiosInstance.post('/admin/clients', data, {
    headers: getAdminHeaders()
  });
  return response.data.client;
};

export const updateClient = async (clientId: number, data: Partial<ClientCreateInput>): Promise<Client> => {
  const response = await axiosInstance.put(`/admin/clients/${clientId}`, data, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const deleteClient = async (clientId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/clients/${clientId}`, {
    headers: getAdminHeaders()
  });
};

// ============================================
// 🗄️ DATABASES API
// ============================================

export const fetchDatabases = async (): Promise<Database[]> => {
  const response = await axiosInstance.get('/admin/databases', {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const createDatabase = async (data: DatabaseCreateInput): Promise<Database> => {
  const response = await axiosInstance.post('/admin/databases', data, {
    headers: getAdminHeaders()
  });
  return response.data.database;
};

export const updateDatabase = async (databaseId: number, data: Partial<DatabaseCreateInput>): Promise<Database> => {
  const response = await axiosInstance.put(`/admin/databases/${databaseId}`, data, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const deleteDatabase = async (databaseId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/databases/${databaseId}`, {
    headers: getAdminHeaders()
  });
};

// Обновление пользователя
export const updateUser = async (userId: number, userData: {
  username?: string;
  email?: string;
  password?: string;
  full_name?: string;
  role?: string;
  client_id?: number;
  is_active?: boolean;
  language?: string; // IT, FR, EN
}): Promise<User> => {
  const response = await axiosInstance.put(`/admin/users/${userId}`, userData, {
    headers: getAdminHeaders()
  });
  return response.data;
};

// ============================================
// 📊 STATISTICS API
// ============================================

export const fetchAdminStats = async (): Promise<AdminStats> => {
  const response = await axiosInstance.get('/admin/stats/overview', {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const fetchRoleStats = async (): Promise<RoleStats> => {
  const response = await axiosInstance.get('/admin/stats/users-by-role', {
    headers: getAdminHeaders()
  });
  return response.data;
};

// ============================================
// 👥 USER ROLE MANAGEMENT
// ============================================

export const promoteToSuperAdmin = async (userId: number): Promise<User> => {
  const response = await axiosInstance.put(`/admin/users/${userId}/promote-to-super-admin`, {}, {
    headers: getAdminHeaders()
  });
  return response.data.user;
};

export const demoteFromSuperAdmin = async (userId: number): Promise<User> => {
  const response = await axiosInstance.put(`/admin/users/${userId}/demote-from-super-admin`, {}, {
    headers: getAdminHeaders()
  });
  return response.data.user;
};

// ============================================
// 🔐 DATABASE ACCESS MANAGEMENT
// ============================================

/**
 * Получить все доступы к базам данных (с опциональными фильтрами)
 * @param userId - Фильтр по ID пользователя (опционально)
 * @param databaseId - Фильтр по ID базы данных (опционально)
 */
export const fetchDatabaseAccess = async (
  userId?: number,
  databaseId?: number
): Promise<DatabaseAccess[]> => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId.toString());
  if (databaseId) params.append('database_id', databaseId.toString());
  
  const url = `/admin/database-access${params.toString() ? '?' + params.toString() : ''}`;
  
  const response = await axiosInstance.get(url, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Получить детальную информацию о доступах конкретного пользователя
 * @param userId - ID пользователя
 */
export const fetchUserDatabaseAccess = async (
  userId: number
): Promise<UserDatabaseAccessSummary> => {
  const response = await axiosInstance.get(`/admin/database-access/user/${userId}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Получить список пользователей с доступом к конкретной базе данных
 * @param databaseId - ID базы данных
 */
export const fetchDatabaseUserAccess = async (
  databaseId: number
): Promise<DatabaseUserAccessSummary> => {
  const response = await axiosInstance.get(`/admin/database-access/database/${databaseId}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Выдать доступ пользователю к базе данных
 * @param input - Данные для создания доступа
 */
export const grantDatabaseAccess = async (
  input: DatabaseAccessCreateInput
): Promise<DatabaseAccess> => {
  // Backend ожидает параметры в query string
  const params = new URLSearchParams({
    user_id: input.user_id.toString(),
    database_id: input.database_id.toString(),
    can_read: input.can_read.toString(),
    can_write: input.can_write.toString()
  });
  
  const response = await axiosInstance.post(`/admin/database-access?${params.toString()}`, {}, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Массовая выдача доступа к нескольким базам данных
 * @param input - Данные для массовой выдачи доступа
 */
export const grantBulkDatabaseAccess = async (
  input: DatabaseAccessBulkInput
): Promise<BulkAccessResult> => {
  const response = await axiosInstance.post('/admin/database-access/bulk', input, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Обновить права доступа к базе данных
 * @param accessId - ID записи доступа
 * @param input - Новые права доступа
 */
export const updateDatabaseAccess = async (
  accessId: number,
  input: DatabaseAccessUpdateInput
): Promise<DatabaseAccess> => {
  const response = await axiosInstance.put(`/admin/database-access/${accessId}`, input, {
    headers: getAdminHeaders()
  });
  return response.data;
};

/**
 * Отозвать конкретный доступ к базе данных
 * @param accessId - ID записи доступа
 */
export const revokeDatabaseAccess = async (accessId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/database-access/${accessId}`, {
    headers: getAdminHeaders()
  });
};

/**
 * Отозвать ВСЕ доступы пользователя к базам данных
 * @param userId - ID пользователя
 */
export const revokeAllUserDatabaseAccess = async (userId: number): Promise<void> => {
  await axiosInstance.delete(`/admin/database-access/user/${userId}/all`, {
    headers: getAdminHeaders()
  });
};

/**
 * Получить список баз данных, к которым текущий пользователь имеет доступ
 */
export const fetchMyDatabaseAccess = async (): Promise<any[]> => {
  const response = await axiosInstance.get('/database-access/me', {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Semantic Layer API
export interface SemanticLayerResponse {
  meta: {
    tenant: string;
    last_modified: string;
    etag: string;
  };
  data: {
    aliases: Array<{
      alias: string;
      canonical: string;
    }>;
  };
}

export interface SemanticLayerUpdate {
  data: {
    aliases: Array<{
      alias: string;
      canonical: string;
    }>;
  };
}

// Semantic Layer запросы ВСЕГДА идут на дефолтный API
export const getSemanticLayer = async (tenant: string): Promise<SemanticLayerResponse> => {
  const response = await semanticLayerApiClient.get(`/admin/semantic-layer/${tenant}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const updateSemanticLayer = async (
  tenant: string,
  data: SemanticLayerUpdate,
  etag: string
): Promise<SemanticLayerResponse> => {
  try {
    const response = await semanticLayerApiClient.put(`/admin/semantic-layer/${tenant}`, data, {
      headers: {
        ...getAdminHeaders(),
        'If-Match': etag
      }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new Error('CONFLICT: File was modified by another user. Please refresh and try again.');
    } else if (error.response?.status === 428) {
      throw new Error('PRECONDITION_REQUIRED: If-Match header is required for existing files.');
    }
    throw error;
  }
};